/**
 * SectionList
 *
 * Displays all sections for the current page with drag-and-drop reordering.
 * Shown in the right panel when no section is selected.
 * Uses native HTML drag-and-drop API (no library dependencies).
 */

import { useState, useCallback, useRef } from 'react';
import { applyStructuralChanges } from '../../api/ai-editor';
import { SectionSuggestions } from './SectionSuggestions';
import { ConvertGalleryModal } from './ConvertGalleryModal';
import type { EditableSection } from '../types';

// =============================================================================
// TYPES
// =============================================================================

interface SectionListProps {
  websiteId: string;
  activePage: string;
  sections: EditableSection[];
  onSectionSelect: (sectionId: string) => void;
  onContentChange: () => void;
}

/**
 * Is this section already driven by the data model?
 *
 * The two models are told apart by their element paths (ADR-009): a converted
 * or added section carries `images.0.url`, a generated one
 * `sections.gallery.images[0]`. Only the latter has anything to convert.
 */
function isEditableGallery(section: EditableSection): boolean {
  return section.elements.some((el) => /^images\.\d+\./.test(el.path));
}

const SECTION_LABELS: Record<string, string> = {
  hero: 'Hero',
  services: 'Services',
  about: 'About',
  testimonials: 'Testimonials',
  gallery: 'Gallery',
  event: 'Event',
  stats: 'Stats',
  credentials: 'Credentials',
  process: 'Process',
  team: 'Team',
  cta: 'Call to Action',
  faq: 'FAQ',
  location: 'Location',
  contact: 'Contact',
};

const SECTION_ICONS: Record<string, string> = {
  hero: '\u2B50',     // star
  services: '\u2699', // gear
  about: '\u{1F4CB}', // clipboard
  testimonials: '\u{1F4AC}', // speech bubble
  gallery: '\u{1F5BC}', // picture
  event: '\u{1F4C5}', // calendar
  stats: '\u{1F4CA}', // chart
  credentials: '\u{1F3C6}', // trophy
  process: '\u{1F504}', // arrows
  team: '\u{1F465}', // people
  cta: '\u{1F4E2}', // loudspeaker
  faq: '\u2753',     // question mark
  location: '\u{1F4CD}', // pin
  contact: '\u2709', // envelope
};

// =============================================================================
// COMPONENT
// =============================================================================

export function SectionList({
  websiteId,
  activePage,
  sections,
  onSectionSelect,
  onContentChange,
}: SectionListProps) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const [isReordering, setIsReordering] = useState(false);
  const dragItemRef = useRef<number | null>(null);

  const handleDragStart = useCallback((index: number) => {
    dragItemRef.current = index;
    setDragIndex(index);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (index !== dragItemRef.current) {
      setDropIndex(index);
    }
  }, []);

  const handleDragLeave = useCallback(() => {
    setDropIndex(null);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    const sourceIndex = dragItemRef.current;
    setDragIndex(null);
    setDropIndex(null);
    dragItemRef.current = null;

    if (sourceIndex === null || sourceIndex === targetIndex || isReordering) return;

    // Build new order array using section IDs (backend expects IDs, not indices)
    const newOrder = sections.map((s) => s.id);
    // Remove source and insert at target
    const [removed] = newOrder.splice(sourceIndex, 1);
    newOrder.splice(targetIndex, 0, removed);

    setIsReordering(true);
    try {
      const result = await applyStructuralChanges(websiteId, [{
        id: crypto.randomUUID(),
        action: 'reorder_sections',
        target: { page: activePage },
        data: { order: newOrder },
      }]);
      if (result.success) {
        onContentChange();
      }
    } catch {
      // Silently fail
    } finally {
      setIsReordering(false);
    }
  }, [sections.length, websiteId, activePage, isReordering, onContentChange]);

  const handleDragEnd = useCallback(() => {
    setDragIndex(null);
    setDropIndex(null);
    dragItemRef.current = null;
  }, []);

  const [convertTarget, setConvertTarget] = useState<string | null>(null);

  return (
    <div className="w-full flex-1 bg-white flex flex-col overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
        <h3 className="text-sm font-semibold text-gray-900">Sections</h3>
        <p className="text-xs text-gray-400 mt-0.5">
          {sections.length} section{sections.length !== 1 ? 's' : ''} · Click to edit · Drag to reorder
        </p>
      </div>

      {/* Section List */}
      <div className="flex-1 overflow-y-auto">
        {sections.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">
            No sections on this page.
          </p>
        ) : (
          <div className="py-1">
            {sections.map((section, index) => {
              const isHero = section.type === 'hero' && index === 0;
              const isDragging = dragIndex === index;
              const isDropTarget = dropIndex === index;
              const canDrag = !isHero && !isReordering;

              return (
                <div
                  key={section.id}
                  draggable={canDrag}
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                  onClick={() => onSectionSelect(section.id)}
                  className={`
                    flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-all
                    ${isDragging ? 'opacity-40' : 'opacity-100'}
                    ${isDropTarget ? 'bg-blue-50 border-t-2 border-blue-400' : 'border-t-2 border-transparent'}
                    hover:bg-gray-50
                  `}
                >
                  {/* Drag Handle */}
                  <div className={`shrink-0 ${canDrag ? 'cursor-grab active:cursor-grabbing' : 'cursor-default opacity-30'}`}>
                    <svg className="w-4 h-4 text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 6a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm0 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm0 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm8-16a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm0 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm0 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4z" />
                    </svg>
                  </div>

                  {/* Section Icon */}
                  <div className="w-7 h-7 rounded-md bg-gray-100 flex items-center justify-center text-sm shrink-0">
                    {SECTION_ICONS[section.type] || '\u2B1B'}
                  </div>

                  {/* Section Info */}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {SECTION_LABELS[section.type] || section.label || section.type}
                    </div>
                    <div className="text-xs text-gray-400">
                      {section.elements.length} element{section.elements.length !== 1 ? 's' : ''}
                      {section.variant ? ` · ${section.variant}` : ''}
                    </div>
                  </div>

                  {/* Convert a frozen gallery so photos can be added and removed */}
                  {section.type === 'gallery' && !isEditableGallery(section) && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setConvertTarget(section.id);
                      }}
                      title="Photos cannot be added or removed until this gallery is converted"
                      className="shrink-0 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md border border-blue-200"
                    >
                      Make editable
                    </button>
                  )}

                  {/* Click arrow */}
                  <svg className="w-4 h-4 text-gray-300 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              );
            })}
          </div>
        )}

        {/* Section Suggestions */}
        <SectionSuggestions
          websiteId={websiteId}
          activePage={activePage}
          onContentChange={onContentChange}
        />
      </div>

      {/* Footer */}
      <div className="px-4 py-2.5 border-t border-gray-200 bg-gray-50">
        <p className="text-xs text-gray-400 text-center">
          Click a section to edit its content
        </p>
      </div>

      {convertTarget && (
        <ConvertGalleryModal
          websiteId={websiteId}
          pageSlug={activePage}
          sectionId={convertTarget}
          onClose={() => setConvertTarget(null)}
          onConverted={onContentChange}
        />
      )}
    </div>
  );
}
